/**
 * JP PMDA package-insert (main label) section viewer.
 *
 * The JP main label is structured XML, so it renders as an HTML section
 * list — but styled like the US/EU viewer: a two-level COLLAPSIBLE
 * accordion (top-level §N rows expand to reveal content + §N.M
 * subsection rows).  English (translated) is the default; a single
 * STICKY floating EN/日本語 toggle (top-right, stays visible while
 * scrolling) flips the whole label's language — no per-section buttons.
 *
 * This component now owns ONLY the drug-detail concerns: fetching by
 * drugId, the Contents-dropdown ``onSections`` callback, and the
 * ``jp-goto-section`` / ``jp-expand-all`` / ``jp-collapse-all`` window
 * events.  The bilingual accordion rendering + EN/日本語 toggle live in
 * the reusable ``JPBilingualSections`` so the Compare view can render the
 * SAME bilingual label from PRE-FETCHED data (its own per-panel toggle)
 * without going through this drug-detail fetch path.
 *
 * Data: GET /drugs/{drugId}/jp-sections (authApi.getDrugJPSections) —
 * each section carries title / content_html (English) + title_ja /
 * content_html_ja (Japanese), plus figures[].image_url (public GCS URL,
 * loaded directly by <img>; bucket is public).  Figure references in
 * the HTML are 【図:gfname】 placeholders we swap for <img>.
 *
 * TOC integration: DrugDetailPage's Contents dropdown dispatches a
 * ``jp-goto-section`` window event; we expand that section (+ its parent
 * if it's a subsection) and scroll to it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { authApi } from '../../services/authApi';
import { findInTab, useTab } from '../../shell/TabContext';
import JPBilingualSections, {
  jpSectionDomId,
  type JPSection,
  type JPFigure,
  type JPDoc,
  type JPLang,
} from './JPBilingualSections';

export { jpSectionDomId };

/** One row of the full canonical PMDA template (for the TOC). */
export interface JPTemplateEntry {
  section_number: string;
  title_en: string;
  title_ja: string;
  present: boolean;
}

const JPSectionView: React.FC<{
  drugId: number;
  /** Which package insert / edition (jp_documents.id) to render; null/undefined
   *  = the current one. The picker lives in the DrugDetailPage header (beside the
   *  doc-type selector), so this component is a pure renderer of the chosen doc. */
  docId?: number | null;
  /** Fired with the present sections + the full canonical template so
   *  the parent (DrugDetailPage) can render the Contents dropdown with
   *  absent sections greyed out. */
  onSections?: (data: {
    present: { section_number: string; title: string; is_parent: boolean }[];
    template: JPTemplateEntry[];
  }) => void;
  /** Language to open in (default English). A Japanese-text keyword hit
   *  (``?source=jpja``) opens the Japanese side; the EN/日本語 toggle still
   *  works afterwards. */
  initialLang?: JPLang | undefined;
  /** Search terms to mark in the rendered content (Japanese-text keyword hit). */
  highlightTerms?: string[] | undefined;
  englishExact?: boolean;
  /** A5 full-label view: all sections mounted, non-interactive headings. */
  alwaysOpen?: boolean | undefined;
  /** Restrict rendered sections (passed to JPBilingualSections when defined). */
  sectionFilter?: string[] | undefined;
  /** When given, the language reset above keys on this instead of
   *  ``highlightTerms`` (so a re-render with a fresh terms array is not a reset). */
  langResetKey?: string | undefined;
  /** Called on actual language transitions (toggle or reset), never on mount. */
  onLangChange?: ((lang: JPLang) => void) | undefined;
}> = ({
  drugId, docId, onSections, initialLang, highlightTerms, englishExact,
  alwaysOpen, sectionFilter, langResetKey, onLangChange,
}) => {
  const [lang, setLang] = useState<JPLang>(initialLang ?? 'en');
  // A new deep link (another hit on the same drug: new terms) re-selects its
  // language, even after the user switched to English in between.
  useEffect(() => {
    if (initialLang) setLang(initialLang);
    // Same dependency-array length in both modes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, langResetKey !== undefined ? [initialLang, langResetKey] : [initialLang, highlightTerms]);
  const prevLangRef = useRef(lang);
  useEffect(() => {
    if (lang !== prevLangRef.current) {
      prevLangRef.current = lang;
      onLangChange?.(lang);
    }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const [doc, setDoc] = useState<JPDoc | null>(null);
  const [sections, setSections] = useState<JPSection[]>([]);
  const [figures, setFigures] = useState<JPFigure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // The events below are window-wide and every shell tab stays mounted: only
  // the visible tab's viewer acts on them, and it scrolls within its own pane.
  const { isActive, rootRef } = useTab();
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    authApi.getDrugJPSections(drugId, docId ?? undefined)
      .then((data: {
        document: JPDoc | null;
        sections: JPSection[];
        figures?: JPFigure[];
        template?: JPTemplateEntry[];
      }) => {
        if (!alive) return;
        setDoc(data.document);
        setSections(data.sections || []);
        setFigures(data.figures || []);
        setLoading(false);
        onSections?.({
          present: (data.sections || []).map((s) => ({
            section_number: s.section_number,
            title: s.title || s.title_ja || `Section ${s.section_number}`,
            is_parent: s.is_parent,
          })),
          template: data.template || [],
        });
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : 'Failed to load JP sections');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [drugId, onSections, docId]);

  // TOC click (from DrugDetailPage) → expand the target (+ parent) + scroll.
  useEffect(() => {
    const handler = (e: Event) => {
      const num = (e as CustomEvent).detail as string;
      if (!num || !isActiveRef.current) return;
      setExpanded((p) => {
        const n = new Set(p);
        n.add(num);
        const parent = num.split('.')[0];
        if (num.includes('.') && parent) n.add(parent);
        return n;
      });
      window.setTimeout(
        () => findInTab(rootRef, jpSectionDomId(num))
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
        80,
      );
    };
    window.addEventListener('jp-goto-section', handler);
    return () => window.removeEventListener('jp-goto-section', handler);
  }, [rootRef]);

  // Expand-all / collapse-all driven by the shared Contents "View All /
  // Close All" button (DrugDetailPage dispatches these events).
  useEffect(() => {
    const expandAll = () => {
      if (isActiveRef.current) setExpanded(new Set(sections.map((s) => s.section_number)));
    };
    const collapseAll = () => {
      if (isActiveRef.current) setExpanded(new Set());
    };
    window.addEventListener('jp-expand-all', expandAll);
    window.addEventListener('jp-collapse-all', collapseAll);
    return () => {
      window.removeEventListener('jp-expand-all', expandAll);
      window.removeEventListener('jp-collapse-all', collapseAll);
    };
  }, [sections]);

  const toggle = (num: string) =>
    setExpanded((p) => {
      const n = new Set(p);
      n.has(num) ? n.delete(num) : n.add(num);
      return n;
    });

  if (loading) {
    return <div className="py-20 text-center text-sm text-slate-500">Loading Japanese label…</div>;
  }
  if (error) {
    return <div className="py-20 text-center text-sm text-red-500">{error}</div>;
  }
  if (!doc || sections.length === 0) {
    return <div className="py-20 text-center text-sm text-slate-500">No Japanese package-insert sections found.</div>;
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      {doc.is_current === false && (
        <div className="px-3 py-1 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex-shrink-0">
          {doc.retired_marker
            ? `Retired package insert (${doc.retired_marker}) — kept for history, not the current label.`
            : `Viewing an earlier edition${doc.package_insert_no ? ` (PIN ${doc.package_insert_no}${doc.revision_date ? ` · ${doc.revision_date}` : ''})` : ''} — not the current label.`}
        </div>
      )}
      <div className="flex-1 min-h-0">
        <JPBilingualSections
          doc={doc}
          sections={sections}
          figures={figures}
          expanded={expanded}
          onToggle={toggle}
          withSectionAnchors
          stickyToggle
          lang={lang}
          onLangChange={setLang}
          highlightTerms={highlightTerms ?? []}
          englishExact={!!englishExact}
          alwaysOpen={alwaysOpen}
          {...(sectionFilter ? { sectionFilter } : {})}
        />
      </div>
    </div>
  );
};

export default JPSectionView;
